import React, { useMemo, useState } from 'react';

import {
  UserOutlined,
  EditOutlined,
  CheckCircleOutlined,
  StopOutlined,
} from '@ant-design/icons';
import { Space, Button, Popconfirm, Tag, message } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '@contexts/admin/AuthContext';
import Can from '@shared/permissions/Can';
import { MultipleValue } from '@shared/table/Rules/component';
import TableIndex from '@shared/table/TableIndex';
import type { SupportRule } from '@shared/table/TableIndex';

import { userService } from '@services/admin/userService';
import { formatDateTime } from '../../../utils/dateTime';

interface UserRow {
  id: string;
  name?: string;
  email?: string;
  created_at?: string;
  disabled_at?: string | null;
  [key: string]: unknown;
}

/**
 * Admin 使用者列表表格
 *
 * 從 List.jsx 抽出的使用者表格（含 Edit / 停用／啟用 操作欄）。
 * List.jsx 僅做 Tabs + Header + Modal 的 orchestration。
 */
export default function AdminUsersTable() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [lastModified, setLastModified] = useState(0);

  const handleToggleStatus = async (record: UserRow) => {
    try {
      const result = record.disabled_at
        ? await userService.enable(record.id)
        : await userService.disable(record.id);
      if (result.success) {
        message.success(t(record.disabled_at ? 'common.message.enable_success' : 'common.message.disable_success'));
        setLastModified(Date.now());
      } else {
        message.error(result.message || t('common.message.operation_failed'));
      }
    } catch (error) {
      console.error('Failed to toggle status:', error);
      message.error(t('common.message.operation_failed'));
    }
  };

  const columns = useMemo(() => ([
    {
      nameKey: 'models.user.name',
      sortable: true,
      sortFieldName: 'users.name',
      wrap: true,
      cell: ({ name }: UserRow) => (
        <Space>
          <UserOutlined />
          {name || '-'}
        </Space>
      ),
    },
    {
      nameKey: 'models.user.email',
      sortable: true,
      sortFieldName: 'users.email',
      wrap: true,
      selector: ({ email }: UserRow) => email || '-',
    },
    {
      nameKey: 'common.created_at',
      sortable: true,
      sortFieldName: 'users.created_at',
      width: '180px',
      selector: ({ created_at }: UserRow) => created_at ? formatDateTime(created_at) : '-',
    },
    {
      nameKey: 'admin.users.edit.account_status.title',
      sortable: false,
      width: '120px',
      cell: ({ disabled_at }: UserRow) => (
        <Tag color={disabled_at ? 'red' : 'green'}>
          {t(disabled_at ? 'admin.users.edit.account_status.disabled' : 'admin.users.edit.account_status.active')}
        </Tag>
      ),
    },
    {
      nameKey: 'common.table.actions',
      persist: true,
      sortable: false,
      width: '220px',
      cell: (record: UserRow) => {
        const isSelf = record.id === currentUser?.id;
        if (isSelf) {
          return (
            <Button
              type="link"
              size="small"
              icon={<EditOutlined />}
              style={{ padding: 0 }}
              onClick={() => navigate('/profile')}
            >
              {t('common.button.edit_profile')}
            </Button>
          );
        }

        return (
          <Space split={<span style={{ color: '#d9d9d9' }}>|</span>} size={4}>
            <Can requiredPermission="users:write">
              <Button
                type="link"
                size="small"
                icon={<EditOutlined />}
                style={{ padding: 0 }}
                onClick={() => navigate(`/users/${record.id}/edit`)}
              >
                {t('common.button.edit')}
              </Button>
            </Can>
            <Can requiredPermission="users:write">
              <Popconfirm
                title={t(record.disabled_at ? 'common.message.confirm_enable' : 'common.message.confirm_disable')}
                description={!record.disabled_at ? t('admin.users.edit.account_status.disable_confirm_description') : undefined}
                onConfirm={() => handleToggleStatus(record)}
                okText={t('common.button.confirm')}
                cancelText={t('common.button.cancel')}
                okButtonProps={!record.disabled_at ? { danger: true } : undefined}
              >
                <Button
                  type="link"
                  size="small"
                  danger={!record.disabled_at}
                  icon={record.disabled_at ? <CheckCircleOutlined /> : <StopOutlined />}
                  style={{ padding: 0 }}
                >
                  {t(record.disabled_at ? 'common.button.enable' : 'common.button.disable')}
                </Button>
              </Popconfirm>
            </Can>
          </Space>
        );
      },
    },
  ]), [t, navigate, currentUser]);

  const supportRules = useMemo<SupportRule[]>(() => [
    {
      label: t('admin.users.list.filters.status'),
      component: MultipleValue,
      inputName: 'users.status',
      defaultPinned: true,
      defaultValue: ['active'],
      createable: false,
      defaultOptions: [
        { label: t('admin.users.edit.account_status.active'), value: 'active' },
        { label: t('admin.users.edit.account_status.disabled'), value: 'disabled' },
      ],
    },
  ], [t]);

  return (
    <TableIndex
      columns={columns}
      searchable={{ match: 'contains' } as any}
      columnsEditable={true}
      tableId="AdminUsersTable"
      defaultQuery={{
        sorting_field: 'users.id',
        sorting_direction: 'desc',
      }}
      supportRules={supportRules}
      service={userService}
      lastModified={lastModified}
    />
  );
}
